import React from 'react';

import { Helmet } from 'react-helmet';
import { Navigate, useParams } from 'react-router-dom';

import { Box, Container } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import SectionNavigation from '~/components/Common/Elements/SectionNavigation';
import SectionNavigationLayout from '~/components/Common/Elements/SectionNavigationLayout';
import { expandTabRoutes, preRegisterSpotlightRoutes } from '~/components/Common/Spotlight/SpotlightProvider';

import APReportsTab, { items as apReportItems } from './APReportsPage';
import AgentHubPage, { items as agentHubItems } from './AgentHubPage';
import BankFilesTab, { items as bankFileItems } from './BankFilesPage';
import BedBankTab, { items as bedbankItems } from './BedBankPage';
import BusinessReportsTab, { items as businessReportItems } from './BusinessReportsPage';
import CarHireTab, { items as carHireItems } from './CarHirePage';
import CruisesTab, { items as cruiseItems } from './CruisePage';
import { items as customLincItems } from './CustomLincPage';
import ExperiencesTab from './ExperiencesTabContainer';
import FlightsTab, { items as flightItems } from './FlightPage';
import FxRatesTab from './FxRates/FxRatesPage';
import LuxPlusTab, { items as luxPlusItems } from './LuxPlusPage';
import MonthReportsTab from './MonthReportsContainer';
import { items as musementItems } from './MusementPage';
import OfferSearchTab from './OffersSearch';
import OthersTab, { items as otherItems } from './Other/OtherPageContainer';
import ToursTab, { items as tourItems } from './TourPage';
import VendorPaymentsTab, { items as vendorPaymentItems } from './VendorPayments/VendorPaymentsPage';

export const TabsMap = {
  'offer-search': {
    label: 'Offer Search',
    slug: 'offer-search',
    group: 'Reports',
    component: OfferSearchTab,
  },
  'business-reports': {
    label: 'Business Reports',
    slug: 'business-reports',
    group: 'Reports',
    component: BusinessReportsTab,
  },
  'ap-reports': {
    label: 'AP Reports',
    slug: 'ap-reports',
    group: 'Reports',
    component: APReportsTab,
  },
  'month-end-reports': {
    label: 'Month End Reports',
    slug: 'month-end-reports',
    group: 'Reports',
    component: MonthReportsTab,
  },
  'vendor-payments': {
    label: 'Vendor Payments',
    slug: 'vendor-payments',
    group: 'Payments & rates',
    component: VendorPaymentsTab,
  },
  'bank-files': {
    label: 'Bank Files',
    slug: 'bank-files',
    group: 'Payments & rates',
    component: BankFilesTab,
  },
  'fx-rates': {
    label: 'FX Rates',
    slug: 'fx-rates',
    group: 'Payments & rates',
    component: FxRatesTab,
  },
  bedbank: {
    label: 'BedBank',
    slug: 'bedbank',
    group: 'Products & partners',
    component: BedBankTab,
  },
  experiences: {
    label: 'Experiences',
    slug: 'experiences',
    group: 'Products & partners',
    component: ExperiencesTab,
  },
  flights: {
    label: 'Flights',
    slug: 'flights',
    group: 'Products & partners',
    component: FlightsTab,
  },
  tours: {
    label: 'Tours',
    slug: 'tours',
    group: 'Products & partners',
    component: ToursTab,
  },
  cruises: {
    slug: 'cruises',
    group: 'Products & partners',
    label: 'Cruises',
    component: CruisesTab,
  },
  carHire: {
    slug: 'car-hire',
    group: 'Products & partners',
    label: 'Car Hire',
    component: CarHireTab,
  },
  LuxPlus: {
    label: 'Lux Plus+',
    slug: 'lux-plus',
    group: 'Products & partners',
    component: LuxPlusTab,
  },
  AgentHub: {
    label: 'AgentHub',
    slug: 'agent-hub',
    group: 'Products & partners',
    component: AgentHubPage,
  },
  other: {
    label: 'Other',
    slug: 'other',
    group: 'Other',
    component: OthersTab,
  },
} as const;

type TabKey = keyof typeof TabsMap;
type FinanceTab = (typeof TabsMap)[TabKey];

const DEFAULT_TAB_SLUG = 'offer-search';
const allTabs = Object.values(TabsMap) as Array<FinanceTab>;
const tabsBySlug = allTabs.reduce<Record<string, FinanceTab>>((acc, tab) => {
  acc[tab.slug] = tab;
  return acc;
}, {});

function getCurrentTab(section?: string): FinanceTab {
  const tab = section ? (tabsBySlug[section] ?? TabsMap[section as TabKey]) : undefined;

  return tab ?? tabsBySlug[DEFAULT_TAB_SLUG];
}

type SidebarItem = { readonly title: string; readonly slug?: string };

function financeSubItems(tabSlug: string, items: ReadonlyArray<SidebarItem>) {
  const tabLabel = Object.values(TabsMap).find((t) => t.slug === tabSlug)?.label ?? tabSlug;
  return items.map((item) => ({
    path: `/finance/${tabSlug}/${item.slug ?? item.title}`,
    label: `Finance / ${tabLabel} / ${item.title}`,
    section: 'Finance',
  }));
}

// Pre-register at module load time so routes are searchable before visiting
preRegisterSpotlightRoutes(expandTabRoutes('/finance', 'Finance', TabsMap));
preRegisterSpotlightRoutes([
  ...financeSubItems('bank-files', bankFileItems),
  ...financeSubItems('ap-reports', apReportItems),
  ...financeSubItems('business-reports', businessReportItems),
  {
    path: '/finance/month-end-reports/xero-trial-balance-sync',
    label: 'Finance / Month End Reports / Xero Trial Balance - Sync',
    section: 'Finance',
  },
  ...financeSubItems('vendor-payments', vendorPaymentItems),
  ...financeSubItems('bedbank', bedbankItems),
  ...financeSubItems('experiences', [...musementItems, ...customLincItems]),
  ...financeSubItems('flights', flightItems),
  ...financeSubItems('tours', tourItems),
  ...financeSubItems('cruises', cruiseItems),
  ...financeSubItems('car-hire', carHireItems),
  ...financeSubItems('lux-plus', luxPlusItems),
  ...financeSubItems('agent-hub', agentHubItems),
  ...financeSubItems('other', otherItems),
]);

export default function FinancePageContainer() {
  const { section, report } = useParams<{ section?: string; report?: string }>();

  const currentTab = getCurrentTab(section);
  const activeTabSlug = currentTab.slug;

  if (section && section !== activeTabSlug) {
    return <Navigate to={`/finance/${activeTabSlug}${report ? `/${report}` : ''}`} replace />;
  }

  const title = `Finance / ${currentTab.label}`;

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <PageHeader title={title} />

      <SectionNavigationLayout
        navigation={
          <SectionNavigation
            label="Finance sections"
            currentTab={activeTabSlug}
            groups={['Reports', 'Payments & rates', 'Products & partners', 'Other'].map((label) => ({
              label,
              items: allTabs
                .filter((tab) => tab.group === label)
                .map((tab) => ({
                  id: tab.slug,
                  label: tab.label,
                  href: `/finance/${tab.slug}`,
                })),
            }))}
          />
        }
      >
        <Box component={currentTab.component} />
      </SectionNavigationLayout>
    </Container>
  );
}
